/**
 * PublicDeliveryNotePage — Phase 2D
 *
 * Page PUBLIQUE accessible via /bl/:token (sans auth).
 *
 * Permet à la clinique de :
 *   - voir le BL (items, total, dates)
 *   - télécharger le PDF
 *   - signer électroniquement (canvas tactile + souris) + saisir nom/rôle
 *   - voir l'état signé (vue read-only)
 *
 * Aucune dépendance externe (canvas natif HTML5).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Loader2, CheckCircle2, AlertCircle, Download, Eraser,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtFCFA = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('fr-FR')} FCFA`);

// ─── Canvas signature ────────────────────────────────────────────────────

const SignatureCanvas = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Adapte au pixel ratio pour qualité retina
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#1F2937';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Fond blanc (important pour PNG opaque)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
    if (isEmpty) setIsEmpty(false);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    onChange?.(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setIsEmpty(true);
    onChange?.(null);
  };

  return (
    <div className="space-y-2">
      <div className="relative border-2 border-dashed border-zinc-300 rounded-lg bg-white overflow-hidden" style={{ height: '180px' }}>
        <canvas
          ref={canvasRef}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          style={{ width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair' }}
          data-testid="signature-canvas"
        />
        {isEmpty && (
          <p className="absolute inset-0 flex items-center justify-center text-zinc-300 italic text-sm pointer-events-none">
            ✍️ Signez ici
          </p>
        )}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={clear} data-testid="signature-clear">
        <Eraser className="w-3.5 h-3.5 mr-1" /> Effacer
      </Button>
    </div>
  );
};

// ─── Page publique ───────────────────────────────────────────────────────

const PublicDeliveryNotePage = ({ token }) => {
  const [dn, setDn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signerRole, setSignerRole] = useState('');
  const [signing, setSigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/b2b/public/delivery-notes/${token}`);
      setDn(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Lien invalide ou expiré.');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submitSignature = async () => {
    if (!signatureData) { toast.error('Veuillez signer dans le cadre ci-dessus.'); return; }
    if (!signerName.trim() || signerName.trim().length < 2) {
      toast.error('Indiquez votre nom (2 caractères minimum).');
      return;
    }
    setSigning(true);
    try {
      await axios.post(`${API}/b2b/public/delivery-notes/${token}/sign`, {
        signature_png_base64: signatureData,
        signed_by_name: signerName.trim(),
        signed_by_role: signerRole.trim() || null,
      });
      toast.success('Bon de livraison signé · merci !');
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur lors de la signature');
    } finally { setSigning(false); }
  };

  const openPdf = () => {
    window.open(`${API}/b2b/public/delivery-notes/${token}/pdf`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-white">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white p-4">
        <div className="max-w-md bg-white rounded-xl shadow-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-bold text-zinc-900 mb-2">Lien invalide</h1>
          <p className="text-sm text-zinc-600">{err}</p>
          <p className="text-xs text-zinc-400 mt-4">Pharmacie Saint Ange Des Amis · Cocody Angré</p>
        </div>
      </div>
    );
  }

  const items = dn.items || [];
  const isSigned = dn.status === 'signed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-zinc-50 py-6 px-3" data-testid="public-bl-page">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* En-tête */}
        <div className="bg-white rounded-xl shadow-md p-5">
          <p className="text-xs text-zinc-500 uppercase tracking-wide">Pharmacie Saint Ange Des Amis</p>
          <h1 className="text-2xl font-bold text-zinc-900 mt-0.5 flex items-center gap-2">
            <FileText className="w-6 h-6 text-emerald-600" />
            Bon de Livraison
          </h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            BL-{dn.id?.slice(0, 8).toUpperCase()} · Émis le {new Date(dn.created_at).toLocaleDateString('fr-FR')}
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-zinc-500">Client</p>
              <p className="font-semibold text-zinc-900">{dn.clinic_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Date livraison</p>
              <p className="font-semibold text-zinc-900">{dn.delivery_date || '—'}</p>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-white rounded-xl shadow-md p-5">
          <h2 className="font-semibold text-zinc-900 mb-3">Articles livrés</h2>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-zinc-500 border-b border-zinc-200">
                  <th className="text-left px-2 py-2">Désignation</th>
                  <th className="text-right px-2 py-2">Qté</th>
                  <th className="text-right px-2 py-2">PU TTC</th>
                  <th className="text-right px-2 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-b border-zinc-100">
                    <td className="px-2 py-2">
                      <p className="text-zinc-900">{it.designation}</p>
                      {it.code && <p className="text-[10px] text-zinc-400 font-mono">code {it.code}</p>}
                    </td>
                    <td className="px-2 py-2 text-right">{it.quantity}{it.unit ? ` ${it.unit}` : ''}</td>
                    <td className="px-2 py-2 text-right text-zinc-600">{fmtFCFA(it.unit_price)}</td>
                    <td className="px-2 py-2 text-right font-medium">{fmtFCFA((it.quantity || 0) * (it.unit_price || 0))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold text-base bg-amber-50">
                  <td colSpan="3" className="px-2 py-2.5 text-right">TOTAL</td>
                  <td className="px-2 py-2.5 text-right text-emerald-700">{fmtFCFA(dn.total_amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {dn.notes && (
            <div className="mt-3 p-3 bg-zinc-50 rounded text-sm text-zinc-700">
              <span className="font-semibold">Notes :</span> {dn.notes}
            </div>
          )}
          <div className="mt-3">
            <Button variant="outline" onClick={openPdf} data-testid="public-bl-pdf">
              <Download className="w-4 h-4 mr-1" /> Télécharger PDF
            </Button>
          </div>
        </div>

        {/* Bloc signature */}
        {isSigned ? (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl shadow-md p-5">
            <div className="flex items-center gap-2 text-emerald-800 mb-3">
              <CheckCircle2 className="w-6 h-6" />
              <h2 className="font-semibold">Bon de livraison signé</h2>
            </div>
            <p className="text-sm text-emerald-900">
              <strong>{dn.signed_by_name}</strong>
              {dn.signed_by_role ? ` (${dn.signed_by_role})` : ''}
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              Signé le {new Date(dn.signed_at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}
            </p>
            {dn.signature_png_base64 && (
              <img
                src={dn.signature_png_base64}
                alt="Signature"
                className="mt-3 max-w-[280px] bg-white border border-emerald-200 rounded"
                data-testid="public-bl-signature-img"
              />
            )}
            <p className="text-xs text-zinc-500 mt-4 italic">
              Vous pouvez télécharger le PDF avec la signature intégrée pour vos archives.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-5 border-2 border-amber-200">
            <h2 className="font-semibold text-zinc-900 mb-2">✍️ Signature de réception</h2>
            <p className="text-xs text-zinc-500 mb-4">
              En signant ci-dessous, vous confirmez avoir reçu les articles listés ci-dessus, conformes à la commande.
            </p>

            <SignatureCanvas onChange={setSignatureData} />

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-zinc-600">Nom du signataire *</Label>
                <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Ex : Dr Konan" data-testid="public-bl-signer-name" />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-600">Fonction (optionnel)</Label>
                <Input value={signerRole} onChange={(e) => setSignerRole(e.target.value)} placeholder="Ex : Pharmacien GMP" data-testid="public-bl-signer-role" />
              </div>
            </div>

            <Button
              onClick={submitSignature}
              disabled={signing || !signatureData || !signerName.trim()}
              className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="public-bl-submit-signature"
            >
              {signing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Valider la signature
            </Button>
          </div>
        )}

        <p className="text-center text-xs text-zinc-400 pt-2">
          Pharmacie Saint Ange Des Amis · Cocody Angré · 07 78 41 52 77
        </p>
      </div>
    </div>
  );
};

export default PublicDeliveryNotePage;
